import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Evenement } from '../../models/evenement.model';
import { EvenementService } from '../../services/evenement.service';
import { ParametreService } from '../../services/parametre.service';
import { MediaService } from '../../services/media.service';
import { Parametre } from '../../models/parametre.model';

type ThemeAffiche = 'dore' | 'corail' | 'sombre';

@Component({
  selector: 'app-generateur-affiche',
  templateUrl: './generateur-affiche.component.html',
  styleUrls: ['./generateur-affiche.component.css']
})
export class GenerateurAfficheComponent implements OnInit {

  @ViewChild('canvasAffiche') canvasRef!: ElementRef<HTMLCanvasElement>;

  evenements: Evenement[] = [];
  evenementChoisiId: number | null = null;
  parametres: Parametre | null = null;

  // ---------- Options de personnalisation du contenu de l'affiche ----------
  afficherPrix = true;
  afficherDate = true;
  afficherLieu = true;
  mention = '';               // ex. "NOUVEAU", "COMPLET", "-20%" — badge en coin, facultatif
  theme: ThemeAffiche = 'dore';

  private couleursParTheme: Record<ThemeAffiche, { accent: string; accentClair: string; fond: string }> = {
    dore:   { accent: '#c9a24b', accentClair: '#e9cd8c', fond: '#05070d' },
    corail: { accent: '#e0663f', accentClair: '#f2a688', fond: '#05070d' },
    sombre: { accent: '#8d96a8', accentClair: '#dfe3ea', fond: '#000000' }
  };

  // ---------- Generation d'image par IA (Pollinations.ai — gratuit, sans cle API) ----------
  promptIA = '';
  imageIA: string | null = null;
  utiliserImageIA = false;
  chargementImageIA = false;
  erreurImageIA = '';

  constructor(
    private evenementService: EvenementService,
    private parametreService: ParametreService,
    private mediaService: MediaService
  ) { }

  ngOnInit(): void {
    this.evenementService.getEvenements().subscribe(data => {
      this.evenements = data;
      this.genererAffiche();
    });
    this.parametreService.getParametres().subscribe(data => this.parametres = data);
  }

  onChoixEvenement(): void {
    const evenement = this.evenements.find(e => e.id === this.evenementChoisiId);
    this.utiliserImageIA = false;
    this.imageIA = null;
    this.erreurImageIA = '';
    this.promptIA = evenement
      ? `affiche de spectacle, ${evenement.titre}, ${evenement.categorie}, ambiance festival méditerranéen, théâtre de nuit, style cinématique, doré`
      : '';
    this.genererAffiche();
  }

  // Demande au backend de generer l'image (il appelle Pollinations.ai lui-meme et la sert
  // depuis notre propre serveur) : evite les blocages CORS et les canvas "souilles" qui
  // empechaient le telechargement quand l'image etait chargee directement depuis le navigateur.
  genererImageIA(): void {
    if (!this.promptIA.trim()) return;

    this.chargementImageIA = true;
    this.erreurImageIA = '';

    this.mediaService.genererImageIA(this.promptIA.trim()).subscribe({
      next: (reponse) => {
        this.chargementImageIA = false;
        if (reponse.success && reponse.url) {
          this.imageIA = reponse.url;
          this.utiliserImageIA = true;
          this.genererAffiche();
        } else {
          this.erreurImageIA = reponse.erreur || "L'image n'a pas pu être générée. Réessayez.";
        }
      },
      error: () => {
        this.chargementImageIA = false;
        this.erreurImageIA = "Impossible de contacter le service IA pour le moment. Réessayez.";
      }
    });
  }

  genererAffiche(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const evenement = this.evenements.find(e => e.id === this.evenementChoisiId);
    const couleurs = this.couleursParTheme[this.theme];

    if (!evenement) {
      ctx.fillStyle = '#0c1424';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#8d96a8';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Choisissez un événement ci-dessus', canvas.width / 2, canvas.height / 2);
      return;
    }

    const dessiner = (image: HTMLImageElement | null) => {
      ctx.fillStyle = couleurs.fond;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (image) {
        const ratio = Math.max(canvas.width / image.width, 320 / image.height);
        const w = image.width * ratio, h = image.height * ratio;
        ctx.drawImage(image, (canvas.width - w) / 2, 0, w, 320);
      }

      const degrade = ctx.createLinearGradient(0, 200, 0, 420);
      degrade.addColorStop(0, 'rgba(5,7,13,0)');
      degrade.addColorStop(1, 'rgba(5,7,13,1)');
      ctx.fillStyle = degrade;
      ctx.fillRect(0, 200, canvas.width, 220);

      ctx.fillStyle = couleurs.accent;
      ctx.font = 'bold 13px Arial';
      ctx.textAlign = 'center';
      const nomFestival = this.parametres?.nomFestival || 'Festival International de Sousse';
      const edition = this.parametres?.edition || '';
      ctx.fillText(nomFestival.toUpperCase() + ' — ' + edition, canvas.width / 2, 355);

      ctx.fillStyle = '#f4eee0';
      ctx.font = 'bold 30px Georgia';
      ctx.fillText(evenement.titre, canvas.width / 2, 400);

      // Ligne "categorie • lieu" : chaque partie est facultative selon les options choisies
      const partiesLigne = [evenement.categorie, this.afficherLieu ? evenement.lieu : null].filter(Boolean);
      if (partiesLigne.length > 0) {
        ctx.fillStyle = couleurs.accentClair;
        ctx.font = '13px Arial';
        ctx.fillText(partiesLigne.join('  •  '), canvas.width / 2, 460);
      }

      if (this.afficherDate && evenement.dateEvent) {
        ctx.fillStyle = '#8d96a8';
        ctx.font = '14px Arial';
        ctx.fillText(evenement.dateEvent, canvas.width / 2, 490);
      }

      if (this.afficherPrix) {
        ctx.fillStyle = '#e0663f';
        ctx.font = 'bold 22px Georgia';
        ctx.fillText(evenement.prix + ' TND', canvas.width / 2, 540);
      }

      // Badge de mention personnalisee (coin superieur droit), seulement si renseignee
      if (this.mention.trim()) {
        const texte = this.mention.trim().toUpperCase();
        ctx.font = 'bold 13px Arial';
        const largeurTexte = ctx.measureText(texte).width;
        const largeurBadge = largeurTexte + 28;

        ctx.save();
        ctx.translate(canvas.width - 24, 24);
        ctx.rotate(0); // badge droit, simple et lisible plutot qu'incline
        ctx.fillStyle = couleurs.accent;
        ctx.beginPath();
        (ctx as any).roundRect
          ? (ctx as any).roundRect(-largeurBadge, 0, largeurBadge, 30, 6)
          : ctx.rect(-largeurBadge, 0, largeurBadge, 30);
        ctx.fill();
        ctx.fillStyle = '#05070d';
        ctx.textAlign = 'center';
        ctx.fillText(texte, -largeurBadge / 2, 20);
        ctx.restore();
      }

      ctx.strokeStyle = couleurs.accent;
      ctx.lineWidth = 3;
      ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);
    };

    const urlImage = (this.utiliserImageIA && this.imageIA) ? this.imageIA : evenement.imageUrl;

    if (urlImage) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => dessiner(img);
      img.onerror = () => dessiner(null);
      img.src = urlImage;
    } else {
      dessiner(null);
    }
  }

  telechargerAffiche(): void {
    const evenement = this.evenements.find(e => e.id === this.evenementChoisiId);
    if (!evenement) {
      alert('Merci de choisir un événement avant de télécharger.');
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    const lien = document.createElement('a');
    lien.download = 'affiche-' + evenement.titre.replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '.png';
    lien.href = canvas.toDataURL('image/png');
    lien.click();
  }
}
